import { useCallback, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';

/**
 * 揽收站点 2.0 灰度开关（对应技术方案 §十七 express2GraySwitch）。
 *
 *   express2Enabled(hub) = wholeSwitch || hubIds.contains(hub)
 *
 * 单一源：后端 pickup common_config `express2_gray_switch`，经 api-server GraphQL
 * `getExpress2GraySwitch` 暴露。前端据此渲染新（2.0：审核池 Tab / 新状态机 / AB / 合并 /
 * dock / 长期揽收多行）或旧 UI。前后端读同一份名单，不再用本地 mock。
 *
 * 调整灰度改后端 common_config.express2_gray_switch 即可（JSON {wholeSwitch,hubIds}，缓存 60s 生效）。
 */
export interface Express2GraySwitch {
  wholeSwitch: boolean;
  hubIds: string[];
}

export const GET_EXPRESS2_GRAY_SWITCH = gql`
  query GetExpress2GraySwitch {
    getExpress2GraySwitch {
      wholeSwitch
      hubIds
    }
  }
`;

interface Express2GrayResponse {
  getExpress2GraySwitch?: {
    wholeSwitch: boolean;
    // 后端 hubIds 为 Long，GraphQL 序列化为字符串
    hubIds: Array<string | number> | null;
  } | null;
}

export function usePickupSiteV2Gray() {
  const { data, loading } = useQuery<Express2GrayResponse>(GET_EXPRESS2_GRAY_SWITCH, {
    fetchPolicy: 'cache-first',
  });
  const cfg = data?.getExpress2GraySwitch;
  const wholeSwitch = cfg?.wholeSwitch ?? false;
  const hubIds = useMemo(() => (cfg?.hubIds ?? []).map(String), [cfg]);

  const express2Enabled = useCallback(
    (hubId?: string | number | null): boolean => {
      if (hubId === null || hubId === undefined || hubId === '') return false;
      return wholeSwitch || hubIds.includes(String(hubId));
    },
    [wholeSwitch, hubIds],
  );

  return { wholeSwitch, hubIds, express2Enabled, loading };
}
